import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContentSection, LinkRow, PageFrame, PageHero } from '../components/PageBlocks';
import { fetchNewsPosts, type NewsPost } from '../lib/supabase';

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).toUpperCase();
    } catch {
        return '';
    }
};

const NewsPage = () => {
    const [posts, setPosts] = useState<NewsPost[] | null>(null);

    useEffect(() => {
        let alive = true;
        fetchNewsPosts().then((data) => {
            if (alive) setPosts(data);
        });
        return () => {
            alive = false;
        };
    }, []);

    const newsItems = posts?.filter((p) => p.category !== 'publishing') ?? null;

    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="minimal"
                numeral="X"
                eyebrow="Signal"
                title="External signal."
                intro="Curated external reporting on AI privacy, sovereignty, security, and the regulatory shift that makes local-first architecture load-bearing."
                proofStrip={[
                    'External signal — not self-promotion.',
                    'Every item links to its primary source.',
                    'Dated and preserved, not rewritten.',
                ]}
            />

            <ContentSection
                sectionId="signal"
                eyebrow="Signal"
                title="What is happening outside — and why it matters here"
                intro="Curated reporting on AI data leaks, regulatory shifts, and sovereignty pressure. Each item links to its primary source."
                borderTop={false}
            >
                {posts === null && <p className="news-empty">Loading…</p>}

                {posts !== null && newsItems && newsItems.length === 0 && (
                    <p className="news-empty">
                        No signal items yet. External reporting will surface here.
                    </p>
                )}

                {newsItems && newsItems.length > 0 && (
                    <ol className="news-list">
                        {newsItems.map((post, idx) => (
                            <li key={post.id} className="news-item">
                                <span className="news-item__num">
                                    {String(idx + 1).padStart(2, '0')}
                                </span>
                                <Link to={`/news/${post.slug}`} className="news-item__card">
                                    {post.image_url && (
                                        <div className="news-item__media">
                                            <img src={post.image_url} alt="" loading="lazy" />
                                        </div>
                                    )}
                                    <div className="news-item__body">
                                        <span className="news-item__meta">
                                            <span className="news-item__date">
                                                {formatDate(post.published_at)}
                                            </span>
                                            {post.source_name && (
                                                <span className="news-item__tag">{post.source_name}</span>
                                            )}
                                        </span>
                                        <h3 className="news-item__title">{post.title}</h3>
                                        {post.excerpt && (
                                            <p className="news-item__excerpt">{post.excerpt}</p>
                                        )}
                                        <span className="news-item__link">
                                            Read our take →
                                        </span>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ol>
                )}
            </ContentSection>

            <ContentSection sectionId="continue" eyebrow="Continue" title="Go deeper">
                <LinkRow
                    links={[
                        { label: 'AvenBox', to: '/avenbox' },
                        { label: 'Technology', to: '/technology' },
                        { label: 'About', to: '/about' },
                        { label: 'Contact', to: '/contact' },
                    ]}
                />
            </ContentSection>
        </PageFrame>
    );
};

export default NewsPage;
